import { useGetPresentersList } from '@/hooks/presenter';
import { isMultilingualVoice } from '@/hooks/useFilteredVoices';
import { AGENT_DEFAULT_NAME } from '@/pages/agents/constants';
import { logger } from '@/services/logger';
import { useAgentEditorStore } from '@/state/root';
import { debounce } from '@/utils/utils';
import { Providers } from '@d-id/client-sdk';
import { PresenterUsecase } from '@d-id/types';
import { useEffect, useMemo, useRef } from 'react';
import { RawUpdatePayload } from '../types';
import { useUpdateAgentById } from './useUpdateAgentById';

export function useEditAgentSync() {
    const agentEditorStore = useAgentEditorStore();

    const { data: presenters } = useGetPresentersList({ usecase: PresenterUsecase.Agent });
    const updateAgentByAgentId = useUpdateAgentById();
    const isFirstRender = useRef(true);
    const latestUpdatesRef = useRef<RawUpdatePayload>();

    const debouncedUpdate = useMemo(
        () =>
            debounce(() => {
                const payload = latestUpdatesRef.current;

                if (!payload) return;

                updateAgentByAgentId(payload as RawUpdatePayload)
                    .then(() => {
                        try {
                            (window as any).DID_AGENTS_API.updateAgent();
                        } catch (e) {
                            logger.error('Failed to update agent from playground', e);
                        }
                    })
                    .catch(logger.error);
            }),
        [logger]
    );

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        // we want to avoid updating while agent didn't fully loaded yet
        if (agentEditorStore.isEditorLoading) return;

        if (!agentEditorStore.isAgentEdit) {
            return;
        }

        const {
            inputAgent: { id, preview_name },
            ttsSelectedVoice,
            ttsSelectedLanguage,
            greetings,
            initAgentState,
            llm: { prompt_customization, ...restLLM },
            baseKnowledge,
            starterQuestions,
            triggers,
            selectedPresenter,
            presenterBackgroundThumbnailUrl,
        } = agentEditorStore;

        // Might happen when, for example, the editor state is reset
        if (!id) return;

        const presenter = presenters?.find(presenter => presenter.id === selectedPresenter);

        const isVoiceMultilingual = ttsSelectedVoice ? isMultilingualVoice(ttsSelectedVoice) : false;
        const isLanguageMultilingual = ttsSelectedLanguage?.toLowerCase() === 'multilingual';
        const useLanguageFromLanguageSelect = isVoiceMultilingual && !isLanguageMultilingual;

        latestUpdatesRef.current = {
            agentId: id,
            updates: {
                agentName: preview_name || AGENT_DEFAULT_NAME,
                ...(ttsSelectedVoice
                    ? {
                          voice: {
                              type: ttsSelectedVoice?.provider as Providers,
                              voice_id: ttsSelectedVoice?.id as string,
                              access: ttsSelectedVoice?.access,
                              language: useLanguageFromLanguageSelect
                                  ? ttsSelectedLanguage || ttsSelectedVoice?.language
                                  : ttsSelectedVoice?.language,
                          },
                      }
                    : {}),
                greetings,
                presenter: presenter
                    ? {
                          ...presenter,
                          thumbnail: presenterBackgroundThumbnailUrl || '',
                      }
                    : undefined,
                starterMessage: starterQuestions,
                ...(!!initAgentState.knowledgeId && {
                    knowledgeId: initAgentState.knowledgeId,
                }),
                llm: {
                    ...restLLM,
                    prompt_customization: {
                        ...prompt_customization,
                        ...(!!baseKnowledge && { prompt_knowledge: baseKnowledge! }),
                    },
                },
                triggers,
            },
        };

        // trigger the debounced only when values changed, and loading completed
        debouncedUpdate(1000);
    }, [
        agentEditorStore.ttsSelectedVoice,
        agentEditorStore.greetings,
        agentEditorStore.triggers,
        agentEditorStore.inputAgent.preview_name,
        agentEditorStore.ttsSelectedLanguage,
        agentEditorStore.inputAgent.id,
        agentEditorStore.llm.template,
        agentEditorStore.llm.temperature,
        agentEditorStore.llm.instructions,
        agentEditorStore.llm.prompt_customization?.max_response_length,
        agentEditorStore.llm.prompt_customization?.personality,
        agentEditorStore.llm.prompt_customization?.role,
        agentEditorStore.llm.prompt_customization?.topics_to_avoid,
        agentEditorStore.llm.prompt_customization?.knowledge_source,
        agentEditorStore.presenterBackgroundColor,
        agentEditorStore.presenterBackgroundSourceUrl,
    ]);
}
